import { User } from "./auth.service";
import axiosService from "./axios.service";
import { atom } from 'jotai';
import { store } from './atom.service';
import { VoteData, VotePayload } from "../models/vote.model";
import { convertDataToIdeaPayload, IdeaPayload, IdeaScorecard } from "./idea.service";
import { CommentPayload } from "./comment.service";
import { convertDataToUser } from "./user.service";
import { liveBoardsAtom } from "./sessionLive.service";
import { Branding, resolveBranding } from "./branding.service";
import { OrganizationSummary } from "./organization.service";

export const currentHackathonAtom = atom<HackathonPayload | null>(null);

export const hackathonsAtom = atom<Record<string, HackathonPayload>>({});

store.set(hackathonsAtom, {});

export interface HackathonOptions {
    inviteCode: string;
    name: string;
    description: string;
    startDate?: Date;
    endDate?: Date;
    ideaSubmissionDeadline?: Date;
    categories: string[];
    maxTeamSize: number;
    minTeamSize: number;
    domain: string;
    participantScores?: boolean;
    blindJudgeScores?: boolean;
    anonymousJudgeScores?: boolean;
    branding?: Branding;
    organization?: string | null;
}

export interface HackathonPayload {
    _id: string;
    slug: string;
    inviteCode: string;
    domain: string;
    preferredAuth: string;
    name: string;
    description: string;
    startDate: Date | null;
    endDate: Date | null;
    ideaSubmissionDeadline: Date | null;
    createdAt?: Date | null;
    updatedAt?: Date | null;
    ideaTemplate?: string;
    categories: string[];
    categoryMeta: Array<{
        // permanent category id; votes are stored against it
        _id?: string;
        label: string;
        description: string;
        judgeOnly: boolean;
    }>;
    participants: User[];
    judges: User[];
    organizers: User[];
    ideas: IdeaPayload[];
    maxTeamSize: number;
    minTeamSize: number;
    // the announced awards; category is the award's name
    winners: Award[];
    // from the live board on an open page, when it's newer than `winners`
    resultsAnnounced?: boolean;
    participantScores?: boolean;
    blindJudgeScores?: boolean;
    anonymousJudgeScores?: boolean;
    votingPeriodOn?: boolean;
    // organizers closed idea submissions early (participants only)
    ideasClosedAt?: Date | null;
    // organizers locked teams: participants can't join, leave or switch
    teamsLocked?: boolean;
    // per-event pricing: custom branding shows only once the event is Pro
    plan: 'free' | 'pro';
    planUpgradedAt: Date | null;
    // has anyone voted yet; from the server, which may not send `results` itself (participants get no scores)
    votesCast?: boolean;
    results?: Array<{
        category: string;
        ideas: Array<IdeaScorecard>
    }>;
    organization: Pick<OrganizationSummary, '_id' | 'name' | 'slug' | 'branding'> | null;
    // the hackathon's own overrides
    branding: Branding;
    // organization branding with the hackathon's overrides applied
    resolvedBranding: Branding;
}

export interface Award {
    category: string;
    ideaId: string;
    // what decided it, shown on the Results card
    basis?: 'judges' | 'participants' | 'organizers';
    // shared that top score with another team; the organizers broke the tie
    tied?: boolean;
}

const shouldReturnCached = new Map<string, boolean>();

const ongoingRequests = new Map<string, Promise<any>>();

const markHackathonUpdated = (id: string) => {
    shouldReturnCached.set(id, true);
    setTimeout(() => shouldReturnCached.set(id, false), 1000);
}


export const createHackathon = async (options: HackathonOptions) => {
    const { data } = await axiosService.post('/api/hackathons/new', options);
    return convertDataToHackathonPayload(data);
}

export const getHackathon = async (id: string) => {
    if (shouldReturnCached.get(id) && store.get(hackathonsAtom)[id]) {
        return store.get(hackathonsAtom)[id];
    }

    let promise;
    if (ongoingRequests.get(id)) {
        promise = ongoingRequests.get(id);
    } else {
        promise = axiosService.get(`/api/hackathons/${id}`);
        ongoingRequests.set(id, promise);
    }

    const { data } = await promise?.catch((e) => {
        ongoingRequests.delete(id);
        throw e;
    });
    ongoingRequests.delete(id);
    return convertDataToHackathonPayload(data);
}

export const updateHackathon = async (id: string, options: HackathonOptions) => {

    const { data } = await axiosService.put(`/api/hackathons/${id}`, options);

    return convertDataToHackathonPayload(data);
}

// judgeCode: from a judge invite link; it stands in for the invite code and makes the person a judge
export const joinHackathon = async (id: string, inviteCode: string, judgeCode?: string) => {
    const { data } = await axiosService.post(`/api/hackathons/join/${id}`, { inviteCode, judgeCode });

    return convertDataToHackathonPayload(data);
}

/** Organizers: the judge invite link's code; renew makes a new one (old links stop working). */
export const getJudgeLinkCode = async (hackathonId: string, renew = false): Promise<string> => {
    const { data } = renew
        ? await axiosService.post(`/api/hackathons/${hackathonId}/judgelink`, { renew: true })
        : await axiosService.get(`/api/hackathons/${hackathonId}/judgelink`);
    return data.code;
}

export const removeParticipantFromHackathon = async (hackathonId: string, email: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/removeparticipant`, { email });

    return convertDataToHackathonPayload(data);
}

export const addOrganizerToHackathon = async (hackathonId: string, email: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/addorganizer`, { email });

    return convertDataToHackathonPayload(data);
}

export const removeOrganizerFromHackathon = async (hackathonId: string, email: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/removeorganizer`, { email });

    return convertDataToHackathonPayload(data);
}

export const addJudgeToHackathon = async (hackathonId: string, email: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/addjudge`, { email });

    return convertDataToHackathonPayload(data);
}

export const removeJudgeFromHackathon = async (hackathonId: string, email: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/removejudge`, { email });

    return convertDataToHackathonPayload(data);
}


export const getMyHackathons = async () => {
    const { data } = await axiosService.get(`/api/hackathons/mine`);
    return data.map(convertDataToHackathonPayload);
}

// keeps the live board's hackathon-wide voting switch and results in step with a change made here
const syncLiveVoting = (data: any) => {
    store.set(liveBoardsAtom, boards => {
        const next = { ...boards };
        [data._id, data.slug].forEach(key => {
            if (key && next[key]) {
                next[key] = { ...next[key], votingPeriodOn: !!data.votingPeriodOn, resultsAnnounced: !!data.winners?.length };
            }
        });
        return next;
    });
}

/** Closes idea submissions now (open = false) for participants, or reopens them. */
export const setIdeasOpen = async (hackathonId: string, open: boolean) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/submissions`, { open });
    return convertDataToHackathonPayload(data);
}

/** The organizers' team lock, independent of voting and dates. */
export const setTeamsLocked = async (hackathonId: string, locked: boolean) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/teamlock`, { locked });
    return convertDataToHackathonPayload(data);
}

export const enableVotingPeriod = async (hackathonId: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/openvoting`);
    syncLiveVoting(data);
    return convertDataToHackathonPayload(data);
}

export const disableVotingPeriod = async (hackathonId: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/closevoting`);
    syncLiveVoting(data);
    return convertDataToHackathonPayload(data);
}

// announces the winners (an empty list takes the announcement back); announcing also closes voting
export const announceWinners = async (hackathonId: string, awards: Award[]) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/winners`, { awards });
    syncLiveVoting(data);
    return convertDataToHackathonPayload(data);
}

export const endVotingPeriod = async (hackathonId: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/endvoting`);
    syncLiveVoting(data);
    return convertDataToHackathonPayload(data);
}

export const convertDataToHackathonPayload = (data: any): HackathonPayload => {
    const payload: HackathonPayload = {
        _id: data._id,
        slug: data.slug,
        inviteCode: data.inviteCode,
        domain: data.domain,
        preferredAuth: data.preferredAuth,
        name: data.name,
        description: data.description,
        startDate: data.startDate ? new Date(data.startDate) : null,
        endDate: data.endDate ? new Date(data.endDate) : null,
        ideaSubmissionDeadline: data.ideaSubmissionDeadline ? new Date(data.ideaSubmissionDeadline) : null,
        createdAt: data.createdAt ? new Date(data.createdAt) : null,
        updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
        ideaTemplate: data.ideaTemplate,
        categories: data.categories,
        categoryMeta: data.categoryMeta,
        participants: data.participants.map(convertDataToUser),
        organizers: data.organizers.map(convertDataToUser),
        judges: data.judges,
        ideas: data.ideas.map(convertDataToIdeaPayload),
        maxTeamSize: data.maxTeamSize,
        minTeamSize: data.minTeamSize,
        winners: (data.winners || []).map((w: any) => ({ category: w.category, ideaId: String(w.ideaId), basis: w.basis, tied: !!w.tied })),
        participantScores: data.participantScores,
        blindJudgeScores: data.blindJudgeScores,
        anonymousJudgeScores: data.anonymousJudgeScores,
        votingPeriodOn: data.votingPeriodOn,
        ideasClosedAt: data.ideasClosedAt ? new Date(data.ideasClosedAt) : null,
        teamsLocked: !!data.teamsLocked,
        plan: data.plan === 'pro' ? 'pro' : 'free',
        planUpgradedAt: data.planUpgradedAt ? new Date(data.planUpgradedAt) : null,
        results: data.results,
        votesCast: data.votesCast,
        organization: data.organization && typeof data.organization === 'object' ? data.organization : null,
        branding: data.branding || {},
        // branding is a Pro feature: a free event keeps the app's own look (organizers can still set it up and preview it)
        resolvedBranding: data.plan === 'pro' ? resolveBranding(data.organization?.branding, data.branding) : {}
    }

    if (payload.categoryMeta.length !== payload.categories.length) {
        payload.categoryMeta = payload.categories.map((category: string) => ({
            label: category,
            description: '',
            judgeOnly: false
        }))
    }

    store.set(hackathonsAtom, {
        ...store.get(hackathonsAtom),
        [data._id]: payload,
        [data.slug]: payload
    });

    if (currentHackathonAtom && store.get(currentHackathonAtom)?._id === data._id) {
        store.set(currentHackathonAtom, payload);
    }

    markHackathonUpdated(data._id);
    markHackathonUpdated(data.slug);
    return payload
}

export const getRawVotes = async (hackathonId: string): Promise<VoteData[]> => {
    const { data } = await axiosService.get(`/api/hackathons/${hackathonId}/votes`);

    return data;
}
